import { useCallback, useEffect, useState } from 'react';
import { useSettings } from '@/entities/settings';
import { defaultThreshold } from '@/entities/strategist';
import type { StrategistHierarchySettings, StrategistTargetAttribute } from '@/entities/strategist';
import { migrateLegacyAttributeFilters } from '@/features/attribute-filters';
import type { StrategistFiltersValue } from '@/widgets/StrategistFilters';

/**
 * Персистируемое за пользователем состояние страницы «Структурные единицы» и обработчики
 * его изменения.
 *
 * Сквозной фильтр (атрибутные фильтры и период), целевая метрика, категория понятий и порог
 * близости читаются из `uiSettings.strategistHierarchy` при инициализации и синхронизируются
 * обратно в стор настроек при изменении — так срез восстанавливается при следующем открытии
 * страницы. Эффекты ресинхронизации подхватывают настройки, обновившиеся извне (например,
 * после загрузки профиля при первом входе).
 *
 * Невыбранная целевая метрика (`undefined`) — законное сохраняемое состояние («Нет»), а не
 * отсутствие настройки: подменять его значением по умолчанию нельзя.
 *
 * Доступные категории и пороги известны только из ответа раздела, поэтому сохранённые
 * категория и порог сверяются с ним отдельно — возвращаемым `handleApplyAvailable`,
 * который страница зовёт на каждый пришедший ответ.
 */
export const useHierarchySettings = () => {
  const { strategistHierarchy, setStrategistHierarchySettings } = useSettings();

  // Сквозной фильтр — одним значением, как его отдаёт виджет фильтров. Сохранённые в старом
  // формате (с ключом `code`) атрибутные фильтры мигрируются на лету: формат общий с дашбордом.
  const [filterValue, setFilterValue] = useState<StrategistFiltersValue>(() => ({
    filters: migrateLegacyAttributeFilters(strategistHierarchy.filters),
    dateFrom: strategistHierarchy.dateFrom,
    dateTo: strategistHierarchy.dateTo,
  }));

  // Целевая метрика: `undefined` — метрика не выбрана («Нет»).
  const [target, setTarget] = useState<StrategistTargetAttribute | undefined>(() => strategistHierarchy.target);

  // Категория и порог: сохранённые значения идут в первый же запрос, а сверяет их с
  // доступными `handleApplyAvailable` — когда придёт ответ с их наборами.
  const [category, setCategory] = useState<string | undefined>(() => strategistHierarchy.category);
  const [threshold, setThreshold] = useState<number | undefined>(() => strategistHierarchy.threshold);

  // Сквозной фильтр: локально и патчем в настройки. Пустое хранится как отсутствующее —
  // иначе набор настроек копил бы пустые значения, ничего не означающие при восстановлении.
  const handleChangeFilters = useCallback(
    (next: StrategistFiltersValue) => {
      setFilterValue(next);
      setStrategistHierarchySettings({
        filters: next.filters.length > 0 ? next.filters : undefined,
        dateFrom: next.dateFrom || undefined,
        dateTo: next.dateTo || undefined,
      });
    },
    [setStrategistHierarchySettings],
  );

  const handleChangeTarget = useCallback(
    (next: StrategistTargetAttribute | undefined) => {
      setTarget(next);
      setStrategistHierarchySettings({ target: next });
    },
    [setStrategistHierarchySettings],
  );

  const handleChangeCategory = useCallback(
    (next: string) => {
      setCategory(next);
      setStrategistHierarchySettings({ category: next });
    },
    [setStrategistHierarchySettings],
  );

  const handleChangeThreshold = useCallback(
    (next: number) => {
      setThreshold(next);
      setStrategistHierarchySettings({ threshold: next });
    },
    [setStrategistHierarchySettings],
  );

  /**
   * Сверяет категорию и порог с доступными значениями из ответа. Сохранённое значение
   * побеждает, но только если оно есть среди доступных: иначе бэк посчитает срез не тем,
   * что выбрано, и страница навсегда останется на скелетоне. Не прошедшее сверку значение
   * заменяется значением из ответа (категория — первая доступная, порог — средний) и сразу
   * перезаписывается в настройки. Совпавшее значение в стор не пишется — вход на страницу
   * не должен сам по себе слать сохранение настроек на бэк.
   *
   * Сверяются сохранённые значения, а не локальные: настройки могли обновиться извне
   * (загрузка профиля) в том же кадре, в котором пришёл ответ, — и локальное состояние
   * ещё не успело их подхватить.
   */
  const handleApplyAvailable = useCallback(
    (categories: string[], thresholds: number[]) => {
      const patch: StrategistHierarchySettings = {};

      const storedCategory = strategistHierarchy.category;
      if (storedCategory === undefined || !categories.includes(storedCategory)) {
        const nextCategory = categories[0];
        if (nextCategory !== storedCategory) {
          setCategory(nextCategory);
          patch.category = nextCategory;
        }
      }

      const storedThreshold = strategistHierarchy.threshold;
      if (storedThreshold === undefined || !thresholds.includes(storedThreshold)) {
        const nextThreshold = defaultThreshold(thresholds);
        if (nextThreshold !== storedThreshold) {
          setThreshold(nextThreshold);
          patch.threshold = nextThreshold;
        }
      }

      if (Object.keys(patch).length > 0) {
        setStrategistHierarchySettings(patch);
      }
    },
    [strategistHierarchy.category, strategistHierarchy.threshold, setStrategistHierarchySettings],
  );

  // Ресинхронизация с настройками, обновившимися извне (загрузка профиля при первом входе).
  // Фильтры старого формата мигрируются и сразу записываются обратно — это переводит
  // сохранённое состояние на текущий контракт без действий пользователя.
  useEffect(() => {
    const source = strategistHierarchy.filters;
    const migrated = migrateLegacyAttributeFilters(source);
    setFilterValue({
      filters: migrated,
      dateFrom: strategistHierarchy.dateFrom,
      dateTo: strategistHierarchy.dateTo,
    });
    const hasLegacy = (source ?? []).some((filter) => !('path' in filter) || !(filter as { path?: string }).path);
    if (hasLegacy) {
      setStrategistHierarchySettings({ filters: migrated });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategistHierarchy.filters, strategistHierarchy.dateFrom, strategistHierarchy.dateTo]);

  useEffect(() => {
    setTarget(strategistHierarchy.target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategistHierarchy.target]);

  useEffect(() => {
    setCategory(strategistHierarchy.category);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategistHierarchy.category]);

  useEffect(() => {
    setThreshold(strategistHierarchy.threshold);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategistHierarchy.threshold]);

  return {
    filterValue,
    target,
    category,
    threshold,
    handleChangeFilters,
    handleChangeTarget,
    handleChangeCategory,
    handleChangeThreshold,
    handleApplyAvailable,
  };
};
